import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  User,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase/config";

interface AuthContextType {
  currentUser: User | null;
  userRole: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<string>;
  logout: () => Promise<any>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const docRef = doc(db, "users", user.uid);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const role = docSnap.data().role as string;
            // Valid user – set both
            setCurrentUser(user);
            setUserRole(role);
          } else {
            // Auth user exists but Firestore doc is missing → treat as logged out
            console.warn(`User ${user.uid} has Auth account but no Firestore doc – signing out`);
            setCurrentUser(null);
            setUserRole(null);
            // Also sign out from Firebase Auth to clean up
            await signOut(auth);
          }
        } catch (err) {
          console.error("Error fetching user doc:", err);
          setCurrentUser(null);
          setUserRole(null);
          await signOut(auth);
        }
      } else {
        // No Auth user at all
        setCurrentUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email: string, password: string): Promise<string> => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const docRef = doc(db, "users", userCredential.user.uid);
    const docSnap = await getDoc(docRef);
    const role = docSnap.exists() ? docSnap.data().role : "coach";
    return role;
  };

  const logout = () => {
    return signOut(auth);
  };

  // Sends a Firebase password-reset email. With Firebase's email-enumeration
  // protection (on by default) this resolves the same way whether or not the
  // address is registered, so callers can show one neutral message and never
  // disclose whether an account exists. Works for every coach, since accounts
  // are created with a real email/password via the createAppUser function.
  const resetPassword = (email: string) => {
    return sendPasswordResetEmail(auth, email);
  };

  return (
    <AuthContext.Provider value={{ currentUser, userRole, loading, login, logout, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
};